import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {
    Positionable,
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {RoadKill} from "../RoadKill";
import {RoadKillSymbol} from "./RoadKillSymbol";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {RoadKillSymbolStates, SimpleReelModelState, SYMBOL_NAME} from "../component/enums/RoadKillEnums";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TimelineLite} from "gsap";

export class RoadKillReelsView extends PIXI.Container implements StageLayer, SimpleReelModelDelegate {

    private static DEFAULT_REEL_SET_WITH_S1: string[] = ["L1", "L2", "L3", "L4", "L5", "S1", "M1", "M2", "M3", "M4", "M5"];
    private static DEFAULT_REEL_SET_WITHOUT_S1: string[] = ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"];

    private _symbolContainer: PIXI.Container;
    private symbolWidth: number = RoadKillGameConfig.SYMBOL_WIDTH;
    private symbolHeight: number = RoadKillGameConfig.SYMBOL_HEIGHT;
    private _reelFrame: PIXI.Sprite;
    private _symbolMask: PIXI.Graphics;
    private _graphicOverlay: PIXI.Graphics;
    private _graphicOverlayContainer: PIXI.Container;
    private _excitementTlSpriteMap: Map<number, TimelineSprite> = new Map<number, TimelineSprite>();
    private _reelModels: SimpleReelModel[];

    constructor() {
        super();
        this.name = "ReelAreaView";
        this.init();
    }

    public createSymbol(symbolName: string, state: SimpleReelModelState, symbolPosition: SimpleReelModelSymbolPosition): Positionable {
        let symbolState: RoadKillSymbolStates = state === SimpleReelModelState.IDLE ? RoadKillSymbolStates.NORMAL : RoadKillSymbolStates.SPIN;
        const symbol: RoadKillSymbol = RoadKillUtils.createSymbol(symbolName, symbolState, symbolPosition.reelID);
        symbol.position.x = symbolPosition.reelID * this.symbolWidth + this.symbolWidth * 0.5;
        this._symbolContainer.addChild(symbol);
        return symbol;
    }

    removeSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void {
        (<PIXI.Sprite>symbolData.positionable).destroy();
    }

    getVisibleSymbolAt(column: number, row: number) {
        const visibleSymbols = this._reelModels[column].getAllVisibleStopSymbols();
        return <RoadKillSymbol>visibleSymbols[row].positionable;
    }

    public handleWildOverlayRestore(): void {
        for (let reelIdx: number = 0; reelIdx < RoadKillGameConfig.REELS_NUM; reelIdx++) {
            for (let symIdx: number = 0; symIdx < RoadKill.model.evaluatedArea[0].length; symIdx++) {
                if (RoadKill.model.evaluatedArea[reelIdx][symIdx] === SYMBOL_NAME.WILD) {
                    this.getVisibleSymbolAt(reelIdx, symIdx).createWildOverlay(reelIdx, symIdx, true);
                }
            }
        }
    }

    public handleSplitOverlayRestore(): void {
        for (let reelIdx: number = 0; reelIdx < RoadKillGameConfig.REELS_NUM; reelIdx++) {
            for (let symIdx: number = 0; symIdx < RoadKill.model.evaluatedArea[0].length; symIdx++) {
                if (RoadKill.model.symbolSize[reelIdx][symIdx] > 1 && !RoadKillGameConfig.WILD_SYMBOLS.includes(RoadKill.model.evaluatedArea[reelIdx][symIdx])) {
                    this.getVisibleSymbolAt(reelIdx, symIdx).setSplit(true).progress(1);
                }
            }
        }
    }

    public modifyFakeReelSetToAddRemoveS1(evaluatedArea: string[][], mode: string, nextMode: string): void {
        const randomSymInx: number = Math.floor((Math.random() * 10));
        if (this.ifRemoveScatter(mode, nextMode)) {
            for (let reelIdx: number = 1; reelIdx <= 3; reelIdx++) {
                if (this.reelModels[reelIdx].reelSet[5] === "S1") {
                    this.reelModels[reelIdx].reelSet[5] = RoadKillReelsView.DEFAULT_REEL_SET_WITHOUT_S1[randomSymInx];
                }
            }
            return;
        }
        if (mode === RoadKillGameMode.NORMAL && nextMode === RoadKillGameMode.NORMAL) {
            for (let reelIdx: number = 1; reelIdx <= 3; reelIdx++) {
                if (this.reelModels[reelIdx].reelSet[5] !== "S1") {
                    this.reelModels[reelIdx].reelSet[5] = "S1";
                }
            }
            return;
        }
        for (let reelIdx: number = 1; reelIdx <= 3; reelIdx++) {
            if (evaluatedArea[reelIdx].includes(SYMBOL_NAME.SW1) || evaluatedArea[reelIdx].includes(SYMBOL_NAME.SW2) || evaluatedArea[reelIdx].includes(SYMBOL_NAME.SW3)) {
                this.reelModels[reelIdx].reelSet[5] = RoadKillReelsView.DEFAULT_REEL_SET_WITHOUT_S1[randomSymInx];
            } else {
                this.reelModels[reelIdx].reelSet[5] = "S1";
            }
        }
    }

    public onReelStop(): void {
        RoadKill.jumpingWildView.onReelStopRemoveMask();
        if (this._symbolContainer.mask) {
            this.reelModels.forEach((rm: SimpleReelModel) => {
                rm.getAllNotVisibleStopSymbols().forEach((sym: SimpleReelModelSymbolData) => {
                    (sym.positionable as RoadKillSymbol).visible = false;
                });
            });
            this._symbolContainer.mask = null;
            this._symbolMask.visible = false;
        }
    }

    public onReelStart(): void {
        RoadKill.jumpingWildView.onReelStartAddMask();
        if (!this._symbolContainer.mask && this._symbolMask) {
            this._symbolContainer.mask = this._symbolMask;
            this._symbolMask.visible = true;
            this.reelModels.forEach((rm: SimpleReelModel) => {
                rm.getAllNotVisibleStopSymbols().forEach((sym: SimpleReelModelSymbolData) => {
                    (sym.positionable as RoadKillSymbol).visible = true;
                });
            });
        }
    }

    public addExcitement(reelId: number) {
        const tl: TimelineLite = new TimelineLite();
        const timelineSprite: TimelineSprite = new TimelineSprite(RoadKill.assetLoader.getTextures("reelExcitement"));
        this.addChild(timelineSprite);
        timelineSprite.anchor.set(0.5);
        timelineSprite.blendMode = PIXI.BLEND_MODES.ADD;
        timelineSprite.alpha = 0;
        timelineSprite.position.set(this.x + (RoadKillGameConfig.SYMBOL_WIDTH * 0.5) + RoadKillGameConfig.SYMBOL_WIDTH * reelId, -40);
        this._excitementTlSpriteMap.set(reelId, timelineSprite);
        tl.add(() => {
            timelineSprite.show();
            timelineSprite.playLoop();
        });
        tl.to(timelineSprite, {duration: 0.2, alpha: 1});
    }

    public removeExcitement(reelId: number) {
        const tl: TimelineLite = new TimelineLite();
        const tls: TimelineSprite | undefined = this._excitementTlSpriteMap.get(reelId);
        if (tls) {
            tl.to(tls, {
                duration: 0.1, alpha: 0, onComplete: () => {
                    tls.stopLoop();
                    tls.hide();
                    tls.parent?.removeChild(tls);
                    this._excitementTlSpriteMap.delete(reelId);
                }
            });
        }
    }

    public turnOffGraphicOverlay(reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const graphicOverlay: PIXI.Graphics = this._graphicOverlayContainer.children[reelId] as PIXI.Graphics;
        if (graphicOverlay && graphicOverlay.visible) {
            tl.to(graphicOverlay, {duration: 0.1, alpha: 0});
            tl.add(() => {
                graphicOverlay.visible = false;
            });
        }
        return tl;
    }

    public turnOnGraphicOverlay(reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const graphicOverlay: PIXI.Graphics = this._graphicOverlayContainer.children[reelId] as PIXI.Graphics;
        if (graphicOverlay && !graphicOverlay.visible) {
            tl.add(() => {
                graphicOverlay.alpha = 0;
                graphicOverlay.visible = true;
            });
            tl.to(graphicOverlay, {duration: 0.1, alpha: 0.2});
        }
        return tl;
    }

    private ifRemoveScatter(mode: string, nextMode: string): boolean {
        let returnValue: boolean = false
        if (mode === RoadKillGameMode.FREESPIN || mode === RoadKillGameMode.SUPER_FREESPIN) {
            returnValue = true;
        } else if ((nextMode === RoadKillGameMode.FREESPIN || nextMode === RoadKillGameMode.SUPER_FREESPIN) && mode === RoadKillGameMode.NORMAL) {
            returnValue = true;
        } else if ((nextMode === RoadKillGameMode.RESPIN) && mode === RoadKillGameMode.NORMAL) {
            returnValue = true;
        }
        return returnValue;
    }

    private createReelFrame(): PIXI.Sprite {
        const sprite = new PIXI.Sprite(RoadKill.assetLoader.getTextures("bgFrame")[0]);
        sprite.anchor.set(0.5);
        sprite.position.set(360, 60);
        return sprite;
    }

    private init() {
        this._reelFrame = this.createReelFrame();
        this._symbolContainer = new PIXI.Container();
        this._symbolContainer.position.set(0, -4);
        this.position.y = 300;

        this._symbolMask = new PIXI.Graphics();
        this._symbolMask.beginFill();
        this._symbolMask.drawRect(-this.symbolWidth * 0.5, -153, this.symbolWidth * 6, this.symbolHeight * 3);
        this._symbolContainer.addChild(this._symbolMask);
        this._symbolContainer.mask = this._symbolMask;
        this._graphicOverlayContainer = new PIXI.Container();
        for (let reel: number = 0; reel < 5; reel++) {
            const graphicOverlay: PIXI.Graphics = new PIXI.Graphics();
            graphicOverlay.beginFill(0x000000);
            graphicOverlay.drawRect(reel * RoadKillGameConfig.SYMBOL_WIDTH, -156, this.symbolWidth, this.symbolHeight * 3);
            graphicOverlay.visible = false;
            this._graphicOverlayContainer.addChild(graphicOverlay);
        }


        this.addChild(this._symbolContainer, this._graphicOverlayContainer, this._reelFrame);

        this._reelModels = [];
        for (let i: number = 0; i < RoadKillGameConfig.REELS_NUM; i++) {
            const reel = new SimpleReelModel(i, this, true);
            if (i === 0 || (i === RoadKillGameConfig.REELS_NUM - 1)) {
                reel.setReelSet(JSON.parse(JSON.stringify(RoadKillReelsView.DEFAULT_REEL_SET_WITHOUT_S1)));
            } else {
                reel.setReelSet(JSON.parse(JSON.stringify(RoadKillReelsView.DEFAULT_REEL_SET_WITH_S1)));
            }
            reel.stepDuration = RoadKillGameConfig.STEP_DURATION;
            reel.stepSize = this.symbolHeight;
            reel.init(RoadKill.model.data.reels[i].concat());

            reel.events?.on(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => {
                //Switches state of all symbols from spin state to normal, when the symbols bounce.
                for (let symbolData of reel.symbols) {
                    (<RoadKillSymbol>symbolData.positionable).setState(RoadKillSymbolStates.NORMAL, "", 0.1);
                }
            });

            reel.events?.on(SimpleReelModelState.START, (data: SimpleReelModelEventData) => {
                //Switches state of all symbols from whatever state to spin when reel wind up is done and the spinning starts.
                for (let symbolData of reel.symbols) {
                    (<RoadKillSymbol>symbolData.positionable).setState(RoadKillSymbolStates.SPIN, "spin", 0.25);
                }
            });
            this._reelModels.push(reel);
        }

    }

    get reelModels(): SimpleReelModel[] {
        return this._reelModels;
    }
}